// Theme tokens (WEB_V2_BUILD.md §4). A theme from catalogueData.js plus the
// business's two colours becomes a flat map of CSS custom properties, set once
// on the page root. Every section reads these (the --t-* contract); legacy
// section components keep reading --theme-* which new themes also fill in.
import {ALIASES, BASE, CLASSIC, THEMES} from "./catalogueData.js";

export const DEFAULT_THEME = "glass";

const BY_NAME = Object.fromEntries(THEMES.map((t) => [t.name, t]));
const CLASSIC_BY_NAME = Object.fromEntries(CLASSIC.map((c) => [c.name, c]));

// The theme a stored name renders as: new names as they are, old names via
// their alias, classic-* as their own legacy folder.
export function canonicalTheme(name) {
    if (typeof name !== "string" || !name) return DEFAULT_THEME;
    if (BY_NAME[name] || CLASSIC_BY_NAME[name]) return name;
    if (ALIASES[name]) return ALIASES[name];
    return DEFAULT_THEME;
}

export function isKnownTheme(name) {
    return typeof name === "string" && (!!BY_NAME[name] || !!CLASSIC_BY_NAME[name] || !!ALIASES[name]);
}

const merge = (base, over) => ({...base, ...(over || {})});

// The full record for a theme, every group filled from BASE.
export function themeRecord(name) {
    const key = canonicalTheme(name);
    const classic = CLASSIC_BY_NAME[key];
    const t = classic ? {...BY_NAME[classic.replacedBy], name: key, label: classic.label, legacy: true, classicFolder: classic.folder} : BY_NAME[key];
    return {
        ...t,
        palette: merge(BASE.palette, t.palette),
        type: merge(BASE.type, t.type),
        shape: merge(BASE.shape, t.shape),
        card: merge(BASE.card, t.card),
        button: merge(BASE.button, t.button),
        badge: merge(BASE.badge, t.badge),
        photo: merge(BASE.photo, t.photo),
        rhythm: merge(BASE.rhythm, t.rhythm),
        store: merge(BASE.store, t.store),
        nav: merge(BASE.nav, t.nav),
        footer: merge(BASE.footer, t.footer),
        hero: merge(BASE.hero, t.hero),
        motion: t.motion || BASE.motion,
    };
}

import {contrast, inkOn, isHex, luminance} from "./colour.js";
export {contrast, inkOn, isHex, luminance};
import {applyLook, lookAttributes} from "./look.js";

// The business's two colours go where the theme decides (TH3). Missing or
// unreadable colours fall back to the theme's own.
function chosenColours(t, brand) {
    const accent = isHex(brand?.accent) ? brand.accent : t.palette.accent;
    const primary = isHex(brand?.primary) ? brand.primary : t.palette.ink;
    return {accent, primary};
}

const px = (v) => (typeof v === "number" ? `${v}` : v);

// CSS custom properties for the page root.
export function themeTokens(name, brandColors = {}, look = null) {
    const t = themeRecord(name);
    const {accent, primary} = chosenColours(t, brandColors);
    const p = t.palette;
    const vars = {
        // Colours
        "--t-bg": p.bg,
        "--t-surface": p.surface,
        "--t-surface-2": p.surface2,
        "--t-ink": p.ink,
        "--t-muted": p.muted,
        "--t-line": p.line,
        "--t-accent": accent,
        "--t-accent-ink": inkOn(accent),
        "--t-primary": primary,
        "--t-primary-ink": inkOn(primary),
        // Type
        "--t-font-display": t.type.display,
        "--t-font-body": t.type.body,
        "--t-font-accent": t.type.accent || t.type.display,
        "--t-display-weight": px(t.type.displayWeight),
        "--t-display-tracking": t.type.displayTracking,
        "--t-display-case": t.type.displayCase,
        "--t-leading": px(t.type.leading),
        "--t-h1": t.type.h1,
        "--t-h2": t.type.h2,
        "--t-h3": t.type.h3,
        // Shape
        "--t-radius": t.shape.radius,
        "--t-radius-sm": t.shape.radiusSm,
        "--t-radius-lg": t.shape.radiusLg,
        "--t-border": t.shape.border,
        "--t-shadow": t.shape.shadow,
        // Cards
        "--t-card-bg": t.card.bg,
        "--t-card-border": t.card.border,
        "--t-card-top-rule": t.card.topRule || t.card.border,
        "--t-card-shadow": t.card.shadow,
        "--t-card-radius": t.card.radius,
        "--t-card-padding": t.card.padding,
        "--t-card-clip": t.card.clip,
        // Buttons
        "--t-btn-bg": t.button.bg,
        "--t-btn-ink": t.button.ink,
        "--t-btn-radius": t.button.radius,
        "--t-btn-border": t.button.border,
        "--t-btn-case": t.button.case,
        "--t-btn-tracking": t.button.tracking,
        "--t-btn-weight": px(t.button.weight),
        "--t-btn-shadow": t.button.shadow,
        "--t-btn-font": t.button.font,
        // Badges
        "--t-badge-bg": t.badge.bg,
        "--t-badge-ink": t.badge.ink,
        "--t-badge-radius": t.badge.radius,
        "--t-badge-border": t.badge.border,
        "--t-badge-case": t.badge.case,
        "--t-badge-rotate": t.badge.rotate,
        "--t-badge-font": t.badge.font,
        // Photos
        "--t-photo-radius": t.photo.radius,
        "--t-photo-filter": t.photo.filter,
        "--t-photo-border": t.photo.border,
        "--t-photo-shadow": t.photo.shadow,
        "--t-photo-rotate": t.photo.rotate,
        // Rhythm
        "--t-section-pad": t.rhythm.sectionPad,
        "--t-gap": t.rhythm.gap,
        "--t-max-width": t.rhythm.maxWidth,
        "--t-alt-bg": t.rhythm.altBg,
        // Store parts (SH2)
        "--t-store-per-row": px(t.store.perRow),
        "--t-store-photo-ratio": t.store.photoRatio,
        "--t-price-weight": px(t.store.priceWeight),
        "--t-price-size": t.store.priceSize,
        "--t-sale-colour": t.store.saleColour,
    };
    // New themes also feed the --theme-* tokens the existing shared section
    // components read, so those sections take on the new look as well.
    // Legacy themes keep their own (registry manifests), untouched.
    if (!t.legacy) {
        Object.assign(vars, {
            "--theme-font-family": t.type.body,
            "--theme-font-family-display": t.type.display,
            "--theme-tracking": "normal",
            "--theme-text-heading": p.ink,
            "--theme-text-body": p.ink,
            "--theme-text-muted": p.muted,
        });
    }
    // "Make it yours": the business's tweaks on top (no-op for legacy themes).
    return applyLook(t, vars, look, brandColors);
}

// Attributes for the page root: which theme, which shapes the CSS keys off.
export function themeAttributes(name, look = null, brandColors = {}) {
    const t = themeRecord(name);
    return {
        "data-theme": t.name,
        "data-theme-legacy": t.legacy ? "true" : "false",
        "data-theme-dark": t.dark ? "true" : "false",
        "data-motion": t.motion,
        "data-store-card": t.store.card,
        "data-store-badge": t.store.badge,
        "data-store-button": t.store.button,
        "data-basket": t.store.basket,
        "data-bands": t.rhythm.bands,
        ...lookAttributes(name, look, brandColors),
    };
}
